import Api, { type CacheConfig } from '@/modules/core/api/api.ts';
import DocumentFile from '@/modules/document/models/file/document-file.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import DocumentOption from '@/modules/common/models/option/document-option.ts';
import DocumentCard from '@/modules/document/models/card/document-card.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { mapMaskFormValue } from '@/modules/document/document-master/utils/mask-value.ts';
import { getDeadlineDate, getDeadlineRequest } from '@/modules/common/utils/deadline.ts';
import type { Mask } from '@/modules/document/document-master/types/mask.ts';
import type { ContractorStep } from '@/modules/document/document-master/types/contractor.ts';
import { DateTime } from 'luxon';
import type { Deadline } from '@/modules/common/types/ui.ts';
import GanttApi from '@/modules/shared/api/gantt-api.ts';
import { DocumentMaskAdapter } from '@/modules/document/adapters/document-mask.adapter.ts';
import { DocumentReportAdapter } from '@/modules/document/adapters/document-report.adapter.ts';
import type { DocumentFileDto } from '@/modules/document/dto/document-file.dto.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import { getCommentMarkdownRequest } from '@/modules/common/utils/comment-markdown-request.util.ts';
import type { DocumentFullDto } from '@/modules/document/dto/document.dto.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

export type AddParameters = {
  typeId: number;
  projectId: number;
  controllerId: number;
  content: string | null;
  comment: string | null;
  deadline: Deadline;
  mask: Mask;
};

export type AddRouteParameters = {
  documentId: number;
  parentDocumentId: number;
  routePointsIds?: number[];
  contractors?: ContractorStep[];
};

export type AddLinkParameters = {
  tableId: TableId;
  documentId: number;
  id: number;
  typeId: number;
};

export type SetControllerParameters = {
  documentId: number;
  loginId: number;
};

export type SetStartDateParameters = {
  documentId: number;
  date: DateTime;
};

/** @deprecated */
export default class DocumentApi {
  static GET_HINTS_COUNT = 500;

  static async getDocument(id: number, signal?: AbortSignal) {
    const response = await GatewayApi.get<DocumentFullDto>(DocumentEndPoint.GetList, 'DOC_ID', id, signal);
    return new DocumentCard(response);
  }

  static async getMasks(masks: string[], { signal }: { signal?: AbortSignal } = {}) {
    const response = await Api.post(DocumentEndPoint.GetMasks, { masks }, signal);
    return DocumentMaskAdapter.mapList(response);
  }

  static async getProgress(id: number, signal?: AbortSignal) {
    const response = await GatewayApi.execute<ItemsDto<{ Выполнение?: number }>>(DocumentEndPoint.GetProgress, {
      filter: GatewayFilter.and(GatewayFilter.eq('DOC_ID', id)),
      signal,
    });
    return response.items[0]['Выполнение'] || 0;
  }

  static async getOptions(query: string, signal?: AbortSignal): Promise<DocumentOption[]> {
    const response: { items: unknown[] } = await Api.post(DocumentEndPoint.GetOptions, { query }, signal);
    return response.items.map((item) => new DocumentOption(item, 'Доступен'));
  }

  static async getFiles(id: number, signal?: AbortSignal, cache?: CacheConfig): Promise<DocumentFile[]> {
    const response: { items: DocumentFileDto[] } = await Api.post(
      DocumentEndPoint.GetFiles,
      { doc_id: id },
      signal,
      true,
      cache
    );
    return response.items.map((item) => new DocumentFile(item));
  }

  static async delete(id: number) {
    return await Api.post(DocumentEndPoint.Delete, { doc_id: id });
  }

  static async stop(id: number, comment: string, commentMarkdown = '') {
    return await Api.post(DocumentEndPoint.Stop, {
      doc_id: id,
      comment,
      ...getCommentMarkdownRequest(commentMarkdown),
    });
  }

  static async add(parameters: AddParameters, signal?: AbortSignal) {
    const mask = parameters.mask.parameters.map(({ number }) => mapMaskFormValue(parameters.mask.formData[number]));

    const response = await Api.post(
      DocumentEndPoint.Add,
      {
        dt_id: parameters.typeId,
        p_id: parameters.projectId,
        controller_id: parameters.controllerId,
        content: parameters.content,
        comment: parameters.comment,
        plan_end_date: getDeadlineDate(parameters.deadline),
        mask_params: mask,
      },
      signal
    );

    return response.doc_id;
  }

  static async addRoute(parameters: AddRouteParameters, signal?: AbortSignal) {
    const routePointsResponsibles = parameters.contractors?.length
      ? parameters.contractors.map((contractor) => {
          if (!contractor.contractor) {
            throw new Error('The contractor is not defined.');
          }

          return {
            RESPONSIBLE_TABLE_ID: contractor.contractor.tableId,
            RESPONSIBLE_ID: contractor.contractor.id,
            ...getDeadlineRequest(contractor.deadline),
            description: contractor.description,
            comment: contractor.unspecified.comment,
          };
        })
      : null;

    return await Api.post(
      DocumentEndPoint.AddRoute,
      {
        DOC_ID: parameters.documentId,
        PARENT_DOC_ID: parameters.parentDocumentId,
        RP_ID: parameters.routePointsIds,
        routePointsResponsibles,
      },
      signal
    );
  }

  static async addLinks(parameters: AddLinkParameters[], signal?: AbortSignal) {
    if (!parameters.length) {
      return;
    }

    return Api.post(
      DocumentEndPoint.AddLinks,
      {
        links: parameters.map((link) => ({
          parent_table_id: link.tableId,
          doc_id: link.documentId,
          parent_id: link.id,
          lt_id: link.typeId,
        })),
      },
      signal
    );
  }

  static async addLink(parameters: AddLinkParameters, signal?: AbortSignal) {
    return await Api.post(
      DocumentEndPoint.AddLink,
      {
        parent_table_id: parameters.tableId,
        doc_id: parameters.documentId,
        parent_id: parameters.id,
        lt_id: parameters.typeId,
      },
      signal
    );
  }

  static async setController(parameters: SetControllerParameters, signal?: AbortSignal) {
    return await Api.post(
      DocumentEndPoint.SetController,
      {
        doc_id: parameters.documentId,
        login_id: parameters.loginId,
      },
      signal
    );
  }

  static async setStartDate(parameters: SetStartDateParameters, signal?: AbortSignal) {
    return await Api.post(
      DocumentEndPoint.SetStartDate,
      {
        doc_id: parameters.documentId,
        start_date: parameters.date.toSQLDate(),
      },
      signal
    );
  }

  static async restartRoute(
    { rpId, doc, full }: { rpId: number; doc: { DOC_ID: number }; full: boolean },
    signal?: AbortSignal
  ) {
    const items = await GanttApi.getItems(doc.DOC_ID);

    const current = items.find((item) => item.rpId === rpId);
    if (!current) {
      throw new Error(`The selected route point was not found.`);
    }

    const targetParentId = current.parentId;

    const routePointsIds: number[] = [];
    let startCollecting = full;

    items.forEach((item) => {
      if (!startCollecting && item.rpId === rpId) {
        startCollecting = true;
      }

      if (startCollecting && item.parentId === targetParentId) {
        routePointsIds.push(item.rpId);
      }
    });

    if (routePointsIds.length === 0) {
      throw new Error('Unable to retrieve any route points for route repeat.');
    }

    await this.addRoute(
      {
        documentId: doc.DOC_ID,
        routePointsIds,
        parentDocumentId: doc.DOC_ID,
      },
      signal
    );
  }

  static async getReports(documentType: number, signal?: AbortSignal) {
    const response = await Api.post(DocumentEndPoint.GetReports, { dt_id: documentType }, signal);
    return DocumentReportAdapter.mapList(response);
  }

  static async getReport(documentId: number, fileId: number, signal?: AbortSignal) {
    const response = await Api.post(DocumentEndPoint.GetReport, { id: documentId, f_id: fileId }, signal);
    return response['request_id'];
  }
}
